import React from "react";
import Eyebrow from "./Eyebrow";

// ── SVG Icons ──────────────────────────────────────────────────────────────

const IconTextInitial = ({
  size = 24,
  color = "currentColor",
}: {
  size?: number;
  color?: string;
}) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M15 5h6" />
    <path d="M15 12h6" />
    <path d="M3 19h18" />
    <path d="m3 12 3.553-7.724a.5.5 0 0 1 .894 0L11 12" />
    <path d="M3.92 10h6.16" />
  </svg>
);

const IconFileHeadphone = ({
  size = 24,
  color = "currentColor",
}: {
  size?: number;
  color?: string;
}) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M4 6.835V4a2 2 0 0 1 2-2h8a2.4 2.4 0 0 1 1.706.706l3.588 3.588A2.4 2.4 0 0 1 20 8v12a2 2 0 0 1-2 2h-.343" />
    <path d="M14 2v5a1 1 0 0 0 1 1h5" />
    <path d="M2 19a2 2 0 0 1 4 0v1a2 2 0 0 1-4 0v-4a6 6 0 0 1 12 0v4a2 2 0 0 1-4 0v-1a2 2 0 0 1 4 0" />
  </svg>
);

const IconBrainCircuit = ({
  size = 24,
  color = "currentColor",
}: {
  size?: number;
  color?: string;
}) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 5a3 3 0 1 0-5.997.125 4 4 0 0 0-2.526 5.77 4 4 0 0 0 .556 6.588A4 4 0 1 0 12 18Z" />
    <path d="M9 13a4.5 4.5 0 0 0 3-4" />
    <path d="M6.003 5.125A3 3 0 0 0 6.401 6.5" />
    <path d="M3.477 10.896a4 4 0 0 1 .585-.396" />
    <path d="M6 18a4 4 0 0 1-1.967-.516" />
    <path d="M12 13h4" />
    <path d="M12 18h6a2 2 0 0 1 2 2v1" />
    <path d="M12 8h8" />
    <path d="M16 8V5a2 2 0 0 1 2-2" />
    <circle cx="16" cy="13" r=".5" />
    <circle cx="18" cy="3" r=".5" />
    <circle cx="20" cy="21" r=".5" />
    <circle cx="20" cy="8" r=".5" />
  </svg>
);

// ── Background Artifact Patterns ───────────────────────────────────────────

const DotGridPattern = ({ color }: { color: string }) => (
  <svg
    style={{
      position: "absolute",
      bottom: 0,
      right: 0,
      opacity: 0.15,
      pointerEvents: "none",
    }}
    width="138"
    height="113"
    viewBox="0 0 138 113"
    xmlns="http://www.w3.org/2000/svg"
  >
    {Array.from({ length: 5 }, (_, row) =>
      Array.from({ length: 6 }, (_, col) => (
        <circle
          key={`${row}-${col}`}
          cx={col * 24 + 10}
          cy={row * 24 + 10}
          r="2.5"
          fill={color}
        />
      )),
    )}
  </svg>
);

const RingPattern = ({ color }: { color: string }) => (
  <svg
    style={{
      position: "absolute",
      bottom: -20,
      right: -20,
      opacity: 0.12,
      pointerEvents: "none",
    }}
    width="163"
    height="163"
    viewBox="0 0 163 163"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle
      cx="81"
      cy="81"
      r="62"
      fill="none"
      stroke={color}
      strokeWidth="1.5"
      strokeDasharray="4 6"
    />
    <circle
      cx="81"
      cy="81"
      r="43"
      fill="none"
      stroke={color}
      strokeWidth="1.5"
      strokeDasharray="4 6"
    />
    <circle
      cx="81"
      cy="81"
      r="23"
      fill="none"
      stroke={color}
      strokeWidth="1.5"
      strokeDasharray="4 6"
    />
  </svg>
);

const CrossPattern = ({ color }: { color: string }) => (
  <svg
    style={{
      position: "absolute",
      bottom: 0,
      right: 0,
      opacity: 0.13,
      pointerEvents: "none",
    }}
    width="150"
    height="125"
    viewBox="0 0 150 125"
    xmlns="http://www.w3.org/2000/svg"
  >
    {Array.from({ length: 5 }, (_, row) =>
      Array.from({ length: 6 }, (_, col) => {
        const cx = col * 26 + 10;
        const cy = row * 26 + 10;
        return (
          <g key={`${row}-${col}`}>
            <line
              x1={cx - 4}
              y1={cy}
              x2={cx + 4}
              y2={cy}
              stroke={color}
              strokeWidth="1.5"
            />
            <line
              x1={cx}
              y1={cy - 4}
              x2={cx}
              y2={cy + 4}
              stroke={color}
              strokeWidth="1.5"
            />
          </g>
        );
      }),
    )}
  </svg>
);

const DiamondPattern = ({ color }: { color: string }) => (
  <svg
    style={{
      position: "absolute",
      bottom: 0,
      right: 0,
      opacity: 0.13,
      pointerEvents: "none",
    }}
    width="150"
    height="125"
    viewBox="0 0 150 125"
    xmlns="http://www.w3.org/2000/svg"
  >
    {Array.from({ length: 5 }, (_, row) =>
      Array.from({ length: 6 }, (_, col) => {
        const cx = col * 26 + 10;
        const cy = row * 26 + 10;
        const s = 5;
        return (
          <polygon
            key={`${row}-${col}`}
            points={`${cx},${cy - s} ${cx + s},${cy} ${cx},${cy + s} ${cx - s},${cy}`}
            fill="none"
            stroke={color}
            strokeWidth="1.2"
          />
        );
      }),
    )}
  </svg>
);

const SpotlightBgIcon = () => (
  <svg
    style={{
      position: "absolute",
      bottom: -16,
      right: -16,
      opacity: 0.07,
      pointerEvents: "none",
    }}
    width="175"
    height="175"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#a881db"
    strokeWidth="1.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M4 6.835V4a2 2 0 0 1 2-2h8a2.4 2.4 0 0 1 1.706.706l3.588 3.588A2.4 2.4 0 0 1 20 8v12a2 2 0 0 1-2 2h-.343" />
    <path d="M14 2v5a1 1 0 0 0 1 1h5" />
    <path d="M2 19a2 2 0 0 1 4 0v1a2 2 0 0 1-4 0v-4a6 6 0 0 1 12 0v4a2 2 0 0 1-4 0v-1a2 2 0 0 1 4 0" />
  </svg>
);

const Modes: React.FC = () => (
  <>
    <style>{`
      .modes-section { padding: 88px 60px; }
      .modes-grid {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 14px;
      }
      .modes-spotlight { grid-row: span 2; }

      @media (max-width: 900px) {
        .modes-section { padding: 64px 28px; }
        .modes-grid { grid-template-columns: repeat(2, 1fr); }
        .modes-spotlight { grid-row: span 1; }
      }

      @media (max-width: 600px) {
        .modes-section { padding: 48px 16px; }
        .modes-grid { grid-template-columns: 1fr; }
        .modes-spotlight { grid-row: span 1; }
      }
    `}</style>
    <section className="modes-section" style={{ background: "#ffffff" }}>
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        {/* Heading */}
        <div style={{ textAlign: "center", marginBottom: 52 }}>
          <Eyebrow color="#0076e9">⚙ Flexible Practice Modes</Eyebrow>
          <h2
            style={{
              fontSize: 44,
              fontWeight: 300,
              letterSpacing: "-0.02em",
              color: "#1a1a1a",
              marginBottom: 14,
            }}
          >
            Practice
            <span className="text-blue-00 italic mx-1">Your Way</span>
          </h2>
          <p
            style={{
              color: "#6b6d70",
              fontSize: 16,
              maxWidth: "48ch",
              margin: "0 auto",
            }}
          >
            Choose input and response modes that fit your environment and
            learning style.
          </p>
        </div>

        {/* Bento — 3×2 grid */}
        <div className="modes-grid">
          {/* Row 1 Col 1: Voice Only */}
          <div
            className="bento-card"
            style={{
              background: "#ffffff",
              border: "1.5px solid #ebe8e8",
              borderRadius: 20,
              padding: 24,
              boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <DotGridPattern color="#a881db" />
            <span
              style={{
                fontSize: 10,
                fontWeight: 700,
                color: "#a881db",
                letterSpacing: 1,
                textTransform: "uppercase",
              }}
            >
              Input Mode
            </span>
            <div
              style={{
                width: 44,
                height: 44,
                borderRadius: 12,
                background: "rgba(168,129,219,0.1)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "14px 0",
              }}
            >
              <IconTextInitial size={22} color="#a881db" />
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginBottom: 4,
              }}
            >
              <span style={{ fontSize: 12, fontWeight: 700, color: "#1a1a1a" }}>
                Voice Only
              </span>
            </div>
            <p style={{ fontSize: 12, color: "#6b6d70", marginBottom: 6 }}>
              Hands-free
            </p>
            <p
              style={{
                fontSize: 13,
                color: "#6b6d70",
                lineHeight: 1.6,
                position: "relative",
                zIndex: 1,
              }}
            >
              Speak your answers naturally. Perfect for building real interview
              confidence.
            </p>
          </div>

          {/* Rows 1+2 Col 2: Voice + Text — SPOTLIGHT */}
          <div
            className="bento-card bento-spotlight modes-spotlight"
            style={{
              background:
                "linear-gradient(160deg, rgba(168,129,219,0.09), #f9fcff)",
              border: "2px solid rgba(168,129,219,0.22)",
              borderRadius: 24,
              padding: 28,
              boxShadow:
                "0 8px 30px rgba(168,129,219,0.1), 0 0 0 1px rgba(255,255,255,0.7) inset",
              display: "flex",
              flexDirection: "column",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <RingPattern color="#a881db" />
            <SpotlightBgIcon />
            <div
              style={{ display: "flex", alignItems: "center", marginBottom: 8 }}
            >
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: "#a881db",
                  letterSpacing: 1,
                  textTransform: "uppercase",
                }}
              >
                Recommended
              </span>
              <span
                style={{
                  marginLeft: "auto",
                  fontSize: 9,
                  fontWeight: 700,
                  color: "#ffffff",
                  background: "#a881db",
                  padding: "2px 8px",
                  borderRadius: 6,
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                }}
              >
                Popular
              </span>
            </div>

            <div
              style={{
                width: 56,
                height: 56,
                borderRadius: 14,
                background:
                  "linear-gradient(135deg, rgba(168,129,219,0.15), rgba(0,193,163,0.1))",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "12px 0 16px",
                position: "relative",
                zIndex: 1,
              }}
            >
              <IconFileHeadphone size={26} color="#a881db" />
            </div>

            <h3
              style={{
                fontSize: 18,
                fontWeight: 700,
                color: "#1a1a1a",
                marginBottom: 6,
                position: "relative",
                zIndex: 1,
              }}
            >
              Voice + Text
            </h3>
            <p
              style={{
                fontSize: 13,
                color: "#6b6d70",
                marginBottom: 12,
                lineHeight: 1.6,
                position: "relative",
                zIndex: 1,
              }}
            >
              The ultimate learning combo. Practice verbally while seeing
              real-time transcription and feedback.
            </p>

            <div style={{ marginTop: "auto", position: "relative", zIndex: 1 }}>
              <p
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: "#a881db",
                  marginBottom: 10,
                  letterSpacing: 0.5,
                }}
              >
                FEATURES INCLUDED
              </p>
              <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                {[
                  "Dual input channels",
                  "Real-time transcription",
                  "Pronunciation feedback",
                  "Confidence metrics",
                ].map((feature) => (
                  <li
                    key={feature}
                    style={{
                      fontSize: 12,
                      color: "#31393c",
                      marginBottom: 6,
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    <span style={{ color: "#00c1a3", fontWeight: 700 }}>✓</span>
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Row 1 Col 3: Text Only */}
          <div
            className="bento-card"
            style={{
              background: "#ffffff",
              border: "1.5px solid #ebe8e8",
              borderRadius: 20,
              padding: 24,
              boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <CrossPattern color="#0076e9" />
            <span
              style={{
                fontSize: 10,
                fontWeight: 700,
                color: "#0076e9",
                letterSpacing: 1,
                textTransform: "uppercase",
              }}
            >
              Response Mode
            </span>
            <div
              style={{
                width: 44,
                height: 44,
                borderRadius: 12,
                background: "rgba(0,118,233,0.1)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "14px 0",
              }}
            >
              <IconTextInitial size={22} color="#0076e9" />
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginBottom: 4,
              }}
            >
              <span style={{ fontSize: 12, fontWeight: 700, color: "#1a1a1a" }}>
                Text Only
              </span>
            </div>
            <p style={{ fontSize: 12, color: "#6b6d70", marginBottom: 6 }}>
              Quick & Quiet
            </p>
            <p
              style={{
                fontSize: 13,
                color: "#6b6d70",
                lineHeight: 1.6,
                position: "relative",
                zIndex: 1,
              }}
            >
              Type your responses for thorough written practice and detailed
              feedback.
            </p>
          </div>

          {/* Row 2 Col 1: Hybrid Input */}
          <div
            className="bento-card"
            style={{
              background: "#ffffff",
              border: "1.5px solid #ebe8e8",
              borderRadius: 20,
              padding: 24,
              boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <DiamondPattern color="#00c1a3" />
            <span
              style={{
                fontSize: 10,
                fontWeight: 700,
                color: "#00c1a3",
                letterSpacing: 1,
                textTransform: "uppercase",
              }}
            >
              Input Mode
            </span>
            <div
              style={{
                width: 44,
                height: 44,
                borderRadius: 12,
                background: "rgba(0,193,163,0.1)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "14px 0",
              }}
            >
              <IconFileHeadphone size={22} color="#00c1a3" />
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginBottom: 4,
              }}
            >
              <span style={{ fontSize: 12, fontWeight: 700, color: "#1a1a1a" }}>
                Voice + Text
              </span>
            </div>
            <p style={{ fontSize: 12, color: "#6b6d70", marginBottom: 6 }}>
              Flexible
            </p>
            <p
              style={{
                fontSize: 13,
                color: "#6b6d70",
                lineHeight: 1.6,
                position: "relative",
                zIndex: 1,
              }}
            >
              Mix and match input modes. Switch between voice and typing based
              on your needs.
            </p>
          </div>

          {/* Row 2 Col 3: Adaptive Response Mode */}
          <div
            className="bento-card"
            style={{
              background:
                "linear-gradient(135deg, rgba(0,193,163,0.06), #f9fcff)",
              border: "1.5px solid rgba(0,193,163,0.2)",
              borderRadius: 20,
              padding: 24,
              boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <DotGridPattern color="#00c1a3" />
            <span
              style={{
                fontSize: 10,
                fontWeight: 700,
                color: "#00c1a3",
                letterSpacing: 1,
                textTransform: "uppercase",
              }}
            >
              Response Mode
            </span>
            <div
              style={{
                width: 44,
                height: 44,
                borderRadius: 12,
                background: "rgba(0,193,163,0.1)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "14px 0",
              }}
            >
              <IconBrainCircuit size={22} color="#00c1a3" />
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginBottom: 4,
              }}
            >
              <span style={{ fontSize: 12, fontWeight: 700, color: "#1a1a1a" }}>
                Adaptive
              </span>
            </div>
            <p style={{ fontSize: 12, color: "#6b6d70", marginBottom: 6 }}>
              Smart & Personalized
            </p>
            <p
              style={{
                fontSize: 13,
                color: "#6b6d70",
                lineHeight: 1.6,
                position: "relative",
                zIndex: 1,
              }}
            >
              AI adjusts difficulty and question selection based on your
              performance.
            </p>
          </div>
        </div>
      </div>
    </section>
  </>
);

export default Modes;
